import { saveAndScan } from 'api';
import { deleteFileByKey, getS3Urls, uploadFile } from 'api/file';
import React, { createContext, ReactNode, useCallback, useState } from 'react';
import {
  Children,
  EntityTypes,
  FileUpload,
  PartialVehicle,
  VRImage,
} from 'types';

export interface FileService {
  addFiles: (files: Array<FileUpload>) => void;
  addUploadedFile: (file: FileUpload) => void;
  uploadFileWithVR: (
    file: FileUpload,
    runCarfax: boolean,
  ) => Promise<PartialVehicle>;
  removeFile: (file: FileUpload) => void;
  clearUploaded: () => void;
  clearCompleted: () => void;
  getJobFileKeys: (entityType: EntityTypes, entityID: number) => Array<string>;
  deleteFile: (fileKey: string) => Promise<void>;
  files: Array<FileUpload>;
}

// THESE ARE EMPTY FUNCTIONS FOR TYPESCRIPT TO USE IN THE CONTEXT SCHEMA.
// THE ACTUAL FUNCTIONS ARE DEFINED IN THE PROVIDER.
export const FileContext = createContext({
  addFiles: (files: Array<FileUpload>) => {
    return;
  },
  addUploadedFile: (file: FileUpload) => {
    return;
  },
  removeFile: (file: FileUpload) => {
    return;
  },
  clearUploaded: () => {
    return;
  },
  clearCompleted: () => {
    return;
  },
  uploadFileWithVR: (
    file: FileUpload,
    runCarfax: boolean,
  ): Promise<PartialVehicle> => {
    return new Promise((resolve) => {
      resolve({} as PartialVehicle);
    });
  },
  getFileKeys: (entityType: EntityTypes, entityID: number): Array<string> => {
    return [];
  },
  deleteFile: (fileKey: string) => {
    return Promise.resolve();
  },
  files: [] as Array<FileUpload>,
});

const FileContextProvider = ({ children }: Children): ReactNode => {
  const [files, setFiles] = useState<Array<FileUpload>>([]);

  const modifyFile = (file: FileUpload) => {
    setFiles((prevFiles) => {
      const newFiles = prevFiles.map((f) => {
        if (f.preview === file.preview) {
          return file;
        }
        return f;
      });
      return newFiles;
    });
  };

  const uploadFiles = useCallback(
    async (fileArr: Array<FileUpload>): Promise<void> => {
      try {
        const res = await getS3Urls(fileArr);
        if (res.status !== 200) {
          const err = await res.json();
          fileArr.forEach((file) => {
            file.metadata.errorMsg = err.message;
            modifyFile(file);
          });
        }
        const metadata = await res.json();

        fileArr.forEach(async (file: FileUpload, index: number) => {
          if (!metadata || !metadata[index]) {
            // should never happen, appeasing TS.
            file.metadata.errorMsg = 'Unknown error uploading files';
            modifyFile(file);
            return;
          }
          // reassociate the metadata with the actual file.
          file.metadata = Object.assign(file.metadata, { ...metadata[index] });
          // upload files which got a url to S3.
          if (!file.metadata.errorMsg && file.metadata.uploadFileURL) {
            const s3res = await uploadFile(file);
            if (!s3res.ok) {
              const err = await s3res.json();
              file.metadata.errorMsg = err.message;
              modifyFile(file);
            } else {
              file.uploaded = true;
              modifyFile(file);
            }
          }
        });
      } catch (e) {
        const err = e as Error;
        fileArr.forEach((file) => {
          file.metadata.errorMsg = err.message;
          modifyFile(file);
        });
      }
    },
    [files],
  );

  const addFiles = useCallback(
    (fileArray: Array<FileUpload>) => {
      setFiles((prevFiles) => [...prevFiles, ...fileArray]);
      uploadFiles(fileArray);
    },
    [files, uploadFiles],
  );

  const addUploadedFile = useCallback(
    (file: FileUpload) => {
      setFiles((prevFiles) => [...prevFiles, ...[file]]);
    },
    [files],
  );

  const uploadFileWithVR = useCallback(
    async (image: FileUpload, runCarfax: boolean): Promise<PartialVehicle> => {
      try {
        const vrImage: VRImage = {
          file: image.metadata,
          runCarfax: runCarfax,
          imageBase64: image.base64,
        };
        const { errors, vehicle, file } = await saveAndScan(vrImage);
        if (file) {
          image.metadata = file;
          image.uploaded = true;
        }
        if (errors && errors.length > 0) {
          image.metadata.errorMsg = errors.join(', ');
        }
        addUploadedFile(image);
        return vehicle;
      } catch (e) {
        const err = e as Error;
        image.metadata.errorMsg = err.message;
        addUploadedFile(image);
        return {} as PartialVehicle;
      }
    },
    [files],
  );

  const removeFile = useCallback(
    (file: FileUpload) => {
      setFiles((prevFiles) =>
        prevFiles.filter((f) => f.preview !== file.preview),
      );
      URL.revokeObjectURL(file.preview);
    },
    [files],
  );

  const clearUploaded = useCallback(() => {
    const remainingFiles = [] as Array<FileUpload>;
    files.forEach((f) => {
      if (!f.uploaded) {
        remainingFiles.push(f);
      } else {
        URL.revokeObjectURL(f.preview);
      }
    });
    setFiles(remainingFiles);
  }, [files]);

  const clearCompleted = useCallback(() => {
    const remainingFiles = [] as Array<FileUpload>;
    files.forEach((f) => {
      if (!f.uploaded && !f.metadata.errorMsg) {
        remainingFiles.push(f);
      } else {
        URL.revokeObjectURL(f.preview);
      }
    });
    setFiles(remainingFiles);
  }, [files]);

  const getFileKeys = useCallback(
    (entityType: EntityTypes, entityID: number) => {
      const keys = [] as Array<string>;
      files.forEach((file) => {
        if (
          file.metadata.entityType === entityType &&
          file.metadata.entityID === entityID &&
          file.metadata.fileKey
        ) {
          keys.push(file.metadata.fileKey);
        }
      });
      return keys;
    },
    [files],
  );

  const deleteFile = useCallback(
    async (fileKey: string) => {
      const res = await deleteFileByKey(fileKey);
      if (!res.ok) {
        throw new Error('Failed to delete file');
      }
      setFiles((prevFiles) =>
        prevFiles.filter((f) => f.metadata.fileKey !== fileKey),
      );
      URL.revokeObjectURL(fileKey);
    },
    [files],
  );

  return (
    <FileContext.Provider
      value={{
        addFiles,
        addUploadedFile,
        uploadFileWithVR,
        removeFile,
        clearUploaded,
        clearCompleted,
        getFileKeys,
        deleteFile,
        files,
      }}
    >
      {children}
    </FileContext.Provider>
  );
};

export default FileContextProvider;
